import { useCallback, useMemo, useRef, useState } from 'react';
import type { VoiceMessage } from '@orborix/voice-client';
import {
  fetchReplySuggestions,
  SuggestError,
  type InspireSuggestion,
  type SuggestMessage,
} from '../../../lib/suggestApi.js';

const MAX_INSPIRE_TURNS = 12;

/**
 * Sugerencias de respuesta on-demand ("Inspirar"). Cuando el user no sabe qué
 * contestar, pide al backend 3 ideas con LLM. Caché por turno del coach:
 * reabrir sobre el mismo último turno no vuelve a pagar tokens; un turno nuevo
 * invalida la caché.
 */
export interface UseInspireReturn {
  open: boolean;
  idx: number;
  suggestions: InspireSuggestion[];
  loading: boolean;
  error: string | null;
  toggleOpen: () => void;
  close: () => void;
  cycle: () => void;
  retry: () => void;
}

export function useInspire(
  messages: ReadonlyArray<VoiceMessage>,
  assistantText: string,
  sessionId: string | null,
): UseInspireReturn {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [suggestions, setSuggestions] = useState<InspireSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqRef = useRef(0);
  const cacheKeyRef = useRef<string | null>(null);

  const lastCoachText = useMemo(
    () => [...messages].reverse().find((m) => m.who === 'bot')?.text ?? '',
    [messages],
  );

  const buildContext = useCallback((): SuggestMessage[] => {
    const out: SuggestMessage[] = [];
    for (const m of messages) {
      if (m.who === 'user') out.push({ role: 'user', content: m.text });
      else if (m.who === 'bot') out.push({ role: 'assistant', content: m.text });
    }
    if (assistantText.trim().length > 0) out.push({ role: 'assistant', content: assistantText });
    return out.filter((m) => m.content.trim().length > 0).slice(-MAX_INSPIRE_TURNS);
  }, [messages, assistantText]);

  const run = useCallback(async (): Promise<void> => {
    const ctx = buildContext();
    if (ctx.length === 0) {
      setSuggestions([]);
      setError('Todavía no hay conversación para sugerir una respuesta.');
      return;
    }
    const reqId = ++reqRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchReplySuggestions(ctx, sessionId ?? undefined);
      if (reqId !== reqRef.current) return;
      setSuggestions(result);
      setIdx(0);
      cacheKeyRef.current = lastCoachText;
    } catch (err) {
      if (reqId !== reqRef.current) return;
      setSuggestions([]);
      cacheKeyRef.current = null;
      setError(err instanceof SuggestError ? err.message : 'No pudimos generar ideas. Probá de nuevo.');
    } finally {
      if (reqId === reqRef.current) setLoading(false);
    }
  }, [buildContext, lastCoachText, sessionId]);

  const toggleOpen = useCallback((): void => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    setIdx(0);
    if (cacheKeyRef.current === lastCoachText && suggestions.length > 0 && !error) return;
    void run();
  }, [open, lastCoachText, suggestions.length, error, run]);

  const close = useCallback(() => setOpen(false), []);
  const cycle = useCallback(
    () => setIdx((i) => (suggestions.length > 0 ? (i + 1) % suggestions.length : 0)),
    [suggestions.length],
  );
  const retry = useCallback(() => void run(), [run]);

  return { open, idx, suggestions, loading, error, toggleOpen, close, cycle, retry };
}
